import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { apiClient } from "../lib/api";
import { toast } from "sonner";
import {
  SoccerBall, SignOut, EnvelopeSimple, Crown,
  ListChecks, ChartLine, Trophy, UsersThree, User as UserIcon,
} from "@phosphor-icons/react";
import { InstallPrompt } from "./InstallPrompt";
import UserAvatar from "./UserAvatar";
import ChatBubble from "./ChatBubble";
import { InstallAppButton } from "./InstallActions";

const navItem =
  "px-3 py-2 text-xs uppercase tracking-[0.18em] font-semibold transition-colors";

export const TopBar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <header
      data-testid="topbar"
      className="sticky top-0 z-50 border-b border-white/10 backdrop-blur-xl bg-[#090A0C]/80"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2 group">
          <SoccerBall size={26} weight="fill" className="text-primary-yellow group-hover:rotate-45 transition-transform" />
          <span className="font-heading text-2xl font-black uppercase tracking-tight">
            Pitch<span className="text-primary-yellow">IQ</span>
          </span>
        </Link>
        <nav className="hidden md:flex items-center gap-1">
          {user && (
            <>
              <NavLink data-testid="nav-fixtures" to="/fixtures" className={({ isActive }) => `${navItem} ${isActive ? "text-primary-yellow" : "text-white/70 hover:text-white"}`}>Fixtures</NavLink>
              <NavLink data-testid="nav-predictions" to="/predictions" className={({ isActive }) => `${navItem} ${isActive ? "text-primary-yellow" : "text-white/70 hover:text-white"}`}>My Picks</NavLink>
              <NavLink data-testid="nav-stats" to="/stats" className={({ isActive }) => `${navItem} ${isActive ? "text-primary-yellow" : "text-white/70 hover:text-white"}`}>Stats</NavLink>
              <NavLink data-testid="nav-leaderboard" to="/leaderboard" className={({ isActive }) => `${navItem} ${isActive ? "text-primary-yellow" : "text-white/70 hover:text-white"}`}>Leaderboard</NavLink>
              <NavLink data-testid="nav-champions" to="/champions" className={({ isActive }) => `${navItem} ${isActive ? "text-primary-yellow" : "text-white/70 hover:text-white"}`}>Past Seasons</NavLink>
              <NavLink data-testid="nav-leagues" to="/leagues" className={({ isActive }) => `${navItem} ${isActive ? "text-primary-yellow" : "text-white/70 hover:text-white"}`}>Leagues</NavLink>
              {!user.is_premier && (
                <NavLink data-testid="nav-pricing" to="/pricing" className={({ isActive }) => `${navItem} ${isActive ? "text-primary-yellow" : "text-primary-yellow/80 hover:text-primary-yellow"} flex items-center gap-1`}>
                  <Crown size={12} weight="fill" /> Go Premier
                </NavLink>
              )}
              {user.is_admin && (
                <NavLink data-testid="nav-admin" to="/admin" className={({ isActive }) => `${navItem} ${isActive ? "text-primary-yellow" : "text-white/70 hover:text-white"}`}>Admin</NavLink>
              )}
            </>
          )}
        </nav>
        <div className="flex items-center gap-3">
          {user ? (
            <>
              <span data-testid="user-points" className="hidden sm:flex items-center gap-2 text-xs uppercase tracking-widest text-white/60">
                <span className="font-mono-num text-primary-yellow text-lg font-bold">{user.total_points}</span> pts
              </span>
              <Link
                data-testid="profile-link"
                to="/profile"
                className="hidden sm:inline-flex items-center gap-2 text-xs uppercase tracking-widest text-white/60 hover:text-primary-yellow border border-white/10 hover:border-primary-yellow pl-1.5 pr-3 py-1 transition-colors"
                title="Profile"
              >
                <UserAvatar avatar={user.avatar} size={26} />
                <span className="flex items-center gap-1">
                  {user.is_premier && <Crown size={11} weight="fill" className="text-primary-yellow" />}
                  {user.username ? `@${user.username}` : "Profile"}
                </span>
              </Link>
              <InstallAppButton small className="hidden md:inline-flex" />
              <button
                data-testid="logout-btn"
                onClick={() => { logout(); navigate("/"); }}
                className="p-2 border border-white/10 hover:border-primary-yellow hover:text-primary-yellow transition-colors"
                aria-label="logout"
              >
                <SignOut size={16} />
              </button>
            </>
          ) : (
            <>
              <Link data-testid="pricing-link" to="/pricing" className="hidden sm:inline-flex text-xs uppercase tracking-widest font-semibold text-white/70 hover:text-primary-yellow px-3 py-2 items-center gap-1">
                <Crown size={12} weight="fill" /> Pricing
              </Link>
              <Link data-testid="login-link" to="/login" className="text-xs uppercase tracking-widest font-semibold text-white/70 hover:text-white px-3 py-2">Sign in</Link>
              <Link data-testid="register-link" to="/register" className="text-xs uppercase tracking-widest font-bold bg-primary-yellow text-black px-4 py-2 hover:-translate-y-0.5 transition-transform shadow-[4px_4px_0px_rgba(255,255,255,0.1)]">
                Get Started
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export const MobileBottomNav = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [ptsThisWeek, setPtsThisWeek] = useState(0);
  const [rankDelta, setRankDelta] = useState(null); // {rank, previous_rank, delta}

  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    const load = () => {
      apiClient.get("/me/points-this-week")
        .then(({ data }) => { if (!cancelled) setPtsThisWeek(data.points || 0); })
        .catch(() => {});
      apiClient.get("/me/rank-delta")
        .then(({ data }) => { if (!cancelled) setRankDelta(data); })
        .catch(() => {});
    };
    load();
    // Re-fetch when route changes so points update after a fresh sync/rescore
    // and every 5 min for live matchdays.
    const t = setInterval(load, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(t); };
  }, [user, location.pathname]);

  if (!user) return null;
  const items = [
    { to: "/fixtures", label: "Fixtures", icon: SoccerBall, testid: "mnav-fixtures" },
    { to: "/predictions", label: "My Picks", icon: ListChecks, testid: "mnav-predictions", badge: ptsThisWeek },
    { to: "/leaderboard", label: "Table", icon: Trophy, testid: "mnav-leaderboard", rankDelta },
    { to: "/leagues", label: "Leagues", icon: UsersThree, testid: "mnav-leagues" },
    { to: "/profile", label: "Profile", icon: UserIcon, testid: "mnav-profile" },
  ];
  return (
    <nav
      data-testid="mobile-bottom-nav"
      className="md:hidden fixed bottom-0 inset-x-0 z-40 border-t border-white/10 bg-[#090A0C]/95 backdrop-blur-xl pb-[env(safe-area-inset-bottom)]"
    >
      <div className="grid grid-cols-5">
        {items.map(({ to, label, icon: Icon, testid, badge, rankDelta: rd }) => {
          const active = location.pathname === to;
          const delta = rd?.delta;
          const showRank = rd && delta !== null && delta !== undefined && delta !== 0;
          return (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              className={`relative flex flex-col items-center justify-center gap-1 py-2 text-[10px] uppercase tracking-widest font-bold transition-colors ${
                active ? "text-primary-yellow" : "text-white/50 hover:text-white/80"
              }`}
            >
              <div className="relative">
                <Icon size={20} weight={active ? "fill" : "regular"} />
                {badge > 0 && (
                  <span
                    data-testid={`${testid}-badge`}
                    className="absolute -top-1.5 -right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-primary-yellow text-black text-[10px] font-mono-num font-bold flex items-center justify-center leading-none"
                    aria-label={`${badge} points this matchweek`}
                  >
                    {badge > 99 ? "99+" : badge}
                  </span>
                )}
                {showRank && (
                  <span
                    data-testid={`${testid}-rank-delta`}
                    className={`absolute -top-1.5 -right-2 min-w-[22px] h-[18px] px-1 rounded-full text-[10px] font-mono-num font-bold flex items-center justify-center leading-none ${
                      delta > 0 ? "bg-emerald-400 text-black" : "bg-red-500 text-white"
                    }`}
                    aria-label={delta > 0 ? `Climbed ${delta} places` : `Fell ${Math.abs(delta)} places`}
                  >
                    {delta > 0 ? `↑${delta}` : `↓${Math.abs(delta)}`}
                  </span>
                )}
              </div>
              {label}
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
};

export const Layout = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  const [resending, setResending] = useState(false);
  const showVerifyBanner =
    user && !user.email_verified && !user.is_admin &&
    !["/verify-email", "/login", "/register", "/forgot-password", "/reset-password"].includes(location.pathname);

  const resend = async () => {
    setResending(true);
    try {
      await apiClient.post("/auth/resend-verification");
      toast.success("Verification email sent — check your inbox");
    } catch {
      toast.error("Couldn't resend — try again in a moment");
    } finally { setResending(false); }
  };

  return (
    <div className="min-h-screen bg-[#090A0C] text-white">
      <TopBar />
      {showVerifyBanner && (
        <div data-testid="verify-banner" className="border-b border-primary-yellow/30 bg-primary-yellow/10 px-4 py-3">
          <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-3 text-xs uppercase tracking-widest">
            <span className="flex items-center gap-2 text-primary-yellow font-bold">
              <EnvelopeSimple size={14} weight="fill" /> Verify your email to start predicting
            </span>
            <button
              onClick={resend}
              disabled={resending}
              data-testid="verify-resend"
              className="border border-primary-yellow text-primary-yellow hover:bg-primary-yellow hover:text-black transition px-4 py-2 font-bold disabled:opacity-50"
            >
              {resending ? "Sending…" : "Resend Link"}
            </button>
          </div>
        </div>
      )}
      <main className={user ? "pb-20 md:pb-0" : ""}>{children}</main>
      <MobileBottomNav />
      <InstallPrompt />
      <footer className="border-t border-white/5 mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 text-xs text-white/40 flex flex-col gap-6">
          <div className="flex flex-wrap gap-x-6 gap-y-2 uppercase tracking-[0.2em]">
            <Link to="/terms" data-testid="footer-terms" className="hover:text-primary-yellow transition-colors">Terms</Link>
            <Link to="/privacy" data-testid="footer-privacy" className="hover:text-primary-yellow transition-colors">Privacy</Link>
            <Link to="/refund" data-testid="footer-refund" className="hover:text-primary-yellow transition-colors">Refund Policy</Link>
            <Link to="/pricing" data-testid="footer-pricing" className="hover:text-primary-yellow transition-colors">Pricing</Link>
            <Link to="/get-started" data-testid="footer-get-started" className="hover:text-primary-yellow transition-colors">Share / Invite</Link>
            <a
              href="mailto:info@pitchiq.win"
              data-testid="footer-contact-email"
              className="hover:text-primary-yellow transition-colors sm:ml-auto"
            >
              info@pitchiq.win
            </a>
          </div>
          <div className="flex flex-col sm:flex-row justify-between gap-3 pt-6 border-t border-white/5">
            <span>© 2026 PitchIQ. Not affiliated with any league or club.</span>
            <span className="uppercase tracking-[0.2em]">Built for the football faithful</span>
          </div>
        </div>
      </footer>
      <ChatBubble />
    </div>
  );
};
